import React, { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { DialogTitle } from '@/components/ui/dialog'
import { SpeakingRole } from '@db8/shared'
import { useHall } from '@/contexts/HallProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { usePoll } from '@/contexts/PollProvider'

type RatingRowProps = {
  criterion: string
  selectedRating: number
  onSelect: (value: number) => void
  selectedGradientClass: string
}

const RatingRow: React.FC<RatingRowProps> = React.memo(
  ({ criterion, selectedRating, onSelect, selectedGradientClass }) => {
    return (
      <div className="flex items-center justify-between my-2">
        <span>{criterion}</span>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((value) => (
            <motion.button
              key={value}
              onClick={() => onSelect(value)}
              className={`px-4 py-2 border border-gray-800 dark:border-white rounded font-bold hover:opacity-50 ${
                value <= selectedRating ? selectedGradientClass : ''
              }`}
            >
              {value}
            </motion.button>
          ))}
        </div>
      </div>
    )
  },
)

export const EvaluationPoll = ({ onNext }) => {
  const { hall } = useHall()
  const { currentUser } = useHallUsers()
  const { submitEvaluationPoll } = usePoll()

  const [ratings, setRatings] = useState({
    [SpeakingRole.Affirmative]: {} as Record<string, number>,
    [SpeakingRole.Negative]: {} as Record<string, number>,
  })

  const [currentTeam, setCurrentTeam] = useState<SpeakingRole.Affirmative | SpeakingRole.Negative>(
    SpeakingRole.Affirmative,
  )

  const handleRatingSelect = useCallback(
    (criterion: string, rating: number) => {
      setRatings((prev) => ({
        ...prev,
        [currentTeam]: {
          ...prev[currentTeam],
          [criterion]: rating,
        },
      }))
    },
    [currentTeam],
  )

  const isCurrentTeamComplete = hall?.evaluationCriteria.every(
    (criterion) => ratings[currentTeam][criterion] > 0,
  )

  const handleNext = () => {
    if (!isCurrentTeamComplete) return

    if (currentTeam === SpeakingRole.Affirmative) {
      setCurrentTeam(SpeakingRole.Negative)
    } else {
      const bothComplete = hall?.evaluationCriteria.every(
        (criterion) =>
          ratings[SpeakingRole.Affirmative][criterion] > 0 &&
          ratings[SpeakingRole.Negative][criterion] > 0,
      )
      if (!bothComplete) return
      submitEvaluationPoll(ratings)
      onNext()
    }
  }

  const teamName =
    currentTeam === SpeakingRole.Affirmative ? hall?.affirmingTeamName : hall?.dissentingTeamName

  const selectedGradientClass =
    currentTeam === SpeakingRole.Affirmative
      ? 'gradient-bg-affirmative text-white'
      : 'gradient-bg-negative text-white'

  return (
    <div className="flex flex-col gap-4">
      <DialogTitle className="text-xl font-semibold mb-2 text-center">
        Evaluate {teamName}
      </DialogTitle>
      <div className="flex flex-col gap-6">
        {hall?.evaluationCriteria.map((criterion) => (
          <RatingRow
            key={criterion}
            criterion={criterion}
            selectedRating={ratings[currentTeam][criterion] || 0}
            onSelect={(value) => handleRatingSelect(criterion, value)}
            selectedGradientClass={selectedGradientClass}
          />
        ))}
      </div>
      <div className="mt-4 flex justify-center">
        <Button
          variant="secondary"
          disabled={!isCurrentTeamComplete}
          onClick={handleNext}
          className="w-full text-black"
        >
          {currentTeam === SpeakingRole.Affirmative ? 'Next' : 'Submit Evaluations'}
        </Button>
      </div>
    </div>
  )
}
